import React, { useState } from "react";
import authFetch from "../../authFetch";
import { haProxyJson } from "./haProxy";

/* ══ SMART HOME SETUP WIZARD ══════════════════════════════ */
/* Guided onboarding for connecting a Home Assistant instance
   to a Command location. Handles:
   1. Connection type selection (Nabu Casa / custom URL)
   2. HA URL + token entry
   3. Test connection
   4. Save credentials (encrypted server-side)                */

const STEPS={TYPE:0,CREDENTIALS:1,TESTING:2,DONE:3};

function SmartHomeSetup({locId,locLabel,state,dispatch,t,isRTL,canManage}){
  const [step,setStep]=useState(STEPS.TYPE);
  const [connType,setConnType]=useState("nabucasa"); /* nabucasa | custom */
  const [haUrl,setHaUrl]=useState("");
  const [token,setToken]=useState("");
  const [testing,setTesting]=useState(false);
  const [error,setError]=useState("");

  if(!canManage) return (
    <div className="sh-setup">
      <div className="sh-setup-empty">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="var(--g300)" strokeWidth="1.5"><path d="M6 22l18-16 18 16"/><path d="M10 20v20h28V20"/><path d="M18 30h12M24 26v8"/></svg>
        <h3>{isRTL?"المنزل الذكي غير مُعد":"Smart Home Not Set Up"}</h3>
        <p>{isRTL?"تواصل مع مالك مساحة العمل لإعداد الاتصال":"Contact the workspace owner to set up the connection."}</p>
      </div>
    </div>
  );

  /* ── Normalize URL ── */
  function normalizeUrl(raw){
    let url=raw.trim().replace(/\/+$/,"");
    if(url&&!/^https?:\/\//i.test(url)) url="https://"+url;
    return url;
  }

  /* ── Build final HA URL from inputs ── */
  function getHaUrl(){
    if(connType==="nabucasa"){
      let slug=haUrl.trim().replace(/\/+$/,"");
      if(slug.includes("://")) return normalizeUrl(slug);
      if(slug&&!slug.includes(".")) slug=slug+".ui.nabu.casa";
      return normalizeUrl("https://"+slug);
    }
    return normalizeUrl(haUrl);
  }

  /* ── Test connection against HA REST API ── */
  async function testConnection(){
    setTesting(true);
    setError("");
    const url=getHaUrl();
    if(!url){setError(isRTL?"أدخل عنوان URL":"Please enter a URL.");setTesting(false);return false;}
    if(!token.trim()){setError(isRTL?"أدخل رمز الوصول":"Please enter an access token.");setTesting(false);return false;}

    /* Use the Command backend proxy to reach HA. This avoids CORS
       entirely — users don't need to touch http.cors_allowed_origins
       in their HA configuration. The proxy attaches the token server
       side and forwards GET /api/ which returns {message: "API running."}
       when credentials are valid. */
    try{
      const data=await haProxyJson({haUrl:url,token:token.trim(),path:"/api/"});
      if(!data?.message){
        setError(isRTL?"استجابة غير متوقعة من الخادم":"Unexpected response from Home Assistant.");
        setTesting(false);
        return false;
      }
      setTesting(false);
      return true;
    }catch(err){
      if(err.status===401){
        setError(isRTL?"رمز الوصول غير صالح":"Invalid access token.");
      }else if(err.status===504||/timed out/i.test(err.message||"")){
        setError(isRTL?"انتهت مهلة الاتصال — تحقق من العنوان":"Connection timed out — check the URL.");
      }else if(err.status===502||/unreachable/i.test(err.message||"")){
        setError(isRTL?"تعذر الوصول إلى Home Assistant — تحقق من العنوان":"Could not reach Home Assistant — check the URL.");
      }else{
        setError(err.message||(isRTL?"فشل الاتصال":"Connection failed"));
      }
      setTesting(false);
      return false;
    }
  }

  /* ── Save credentials and connection config ── */
  async function saveConnection(){
    setTesting(true);
    setError("");
    const url=getHaUrl();
    try{
      /* 1. Store encrypted token via API */
      const res=await authFetch("/api/ha-credentials",{
        method:"POST",
        headers:{"Content-Type":"application/json"},
        body:JSON.stringify({locId,token:token.trim()}),
      });
      const body=await res.json();
      if(!res.ok) throw new Error(body.error||"Failed to save token");

      /* 2. Update smartHomeConnections config */
      const conns={...(state.smartHomeConnections||{})};
      conns[locId]={
        locId,
        haUrl:url,
        connectionType:connType,
        status:"connected",
        label:locLabel,
        connectedAt:new Date().toISOString(),
        lastVerified:new Date().toISOString(),
      };
      dispatch({type:"SET_CONFIG_KEY",payload:{key:"smartHomeConnections",value:conns}});

      setStep(STEPS.DONE);
    }catch(err){
      setError(err.message||(isRTL?"فشل حفظ الاتصال":"Failed to save connection"));
    }finally{
      setTesting(false);
    }
  }

  /* ── Step: Connection Type ── */
  if(step===STEPS.TYPE) return (
    <div className="sh-setup">
      <div className="sh-setup-header">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="var(--gold)" strokeWidth="1.5"><path d="M5 18l15-12 15 12"/><path d="M8 16v16h24V16"/><rect x="15" y="24" width="10" height="8"/></svg>
        <h2>{isRTL?"إعداد المنزل الذكي":"Set Up Smart Home"}</h2>
        <p className="sh-setup-loc">{locLabel}</p>
      </div>
      <p className="sh-setup-desc">
        {isRTL
          ?"اربط Home Assistant بهذا الموقع للتحكم بأجهزتك الذكية."
          :"Connect Home Assistant to this location to control your smart devices."}
      </p>

      <div className="sh-type-cards">
        <button className={`sh-type-card${connType==="nabucasa"?" sh-type-active":""}`} onClick={()=>setConnType("nabucasa")}>
          <div className="sh-type-radio">{connType==="nabucasa"&&<div className="sh-type-radio-dot"/>}</div>
          <div>
            <strong>Nabu Casa</strong>
            <p>{isRTL?"اتصال سحابي آمن (موصى به)":"Secure cloud connection (recommended)"}</p>
          </div>
        </button>
        <button className={`sh-type-card${connType==="custom"?" sh-type-active":""}`} onClick={()=>setConnType("custom")}>
          <div className="sh-type-radio">{connType==="custom"&&<div className="sh-type-radio-dot"/>}</div>
          <div>
            <strong>{isRTL?"عنوان مخصص":"Custom URL"}</strong>
            <p>{isRTL?"اتصال مباشر بخادم HA":"Direct connection to HA server"}</p>
          </div>
        </button>
      </div>

      <button className="btn btn-p sh-btn" onClick={()=>setStep(STEPS.CREDENTIALS)}>
        {isRTL?"التالي":"Next"}
      </button>
    </div>
  );

  /* ── Step: Credentials ── */
  if(step===STEPS.CREDENTIALS) return (
    <div className="sh-setup">
      <div className="sh-setup-header">
        <h2>{isRTL?"بيانات الاتصال":"Connection Details"}</h2>
        <p className="sh-setup-loc">{locLabel}</p>
      </div>

      <div className="sh-form">
        <label className="fl">
          {connType==="nabucasa"
            ?(isRTL?"رابط Nabu Casa أو المعرف":"Nabu Casa URL or slug")
            :(isRTL?"عنوان Home Assistant":"Home Assistant URL")}
        </label>
        <input
          className="fi"
          type="text"
          placeholder={connType==="nabucasa"?"xxxxx.ui.nabu.casa":"https://ha.example.com:8123"}
          value={haUrl}
          onChange={e=>setHaUrl(e.target.value)}
          dir="ltr"
        />

        <label className="fl" style={{marginTop:"1rem"}}>
          {isRTL?"رمز الوصول طويل الأمد":"Long-Lived Access Token"}
        </label>
        <input
          className="fi"
          type="password"
          placeholder={isRTL?"الصق الرمز هنا":"Paste token here"}
          value={token}
          onChange={e=>setToken(e.target.value)}
          dir="ltr"
        />
        <p className="sh-hint">
          {isRTL
            ?"أنشئ رمزاً من إعدادات HA: الملف الشخصي → رموز الوصول طويلة الأمد"
            :"Create a token in HA: Profile → Long-Lived Access Tokens"}
        </p>

        {error && <div className="sh-error">{error}</div>}
      </div>

      <div className="sh-btn-row">
        <button className="btn btn-s sh-btn" onClick={()=>{setStep(STEPS.TYPE);setError("");}}>
          {isRTL?"رجوع":"Back"}
        </button>
        <button className="btn btn-p sh-btn" disabled={testing} onClick={async()=>{
          const ok=await testConnection();
          if(ok) setStep(STEPS.TESTING);
        }}>
          {testing?(isRTL?"جارٍ الاختبار...":"Testing..."):(isRTL?"اختبار الاتصال":"Test Connection")}
        </button>
      </div>
    </div>
  );

  /* ── Step: Test passed — confirm save ── */
  if(step===STEPS.TESTING) return (
    <div className="sh-setup">
      <div className="sh-setup-header">
        <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="var(--green,#22c55e)" strokeWidth="1.5"><circle cx="20" cy="20" r="16"/><path d="M12 20l5 5 11-11"/></svg>
        <h2>{isRTL?"تم الاتصال بنجاح":"Connection Successful"}</h2>
        <p className="sh-setup-loc">{locLabel}</p>
      </div>
      <p className="sh-setup-desc">
        {isRTL
          ?"تم التحقق من الاتصال بـ Home Assistant. احفظ لإكمال الإعداد."
          :"Home Assistant connection verified. Save to complete setup."}
      </p>

      {error && <div className="sh-error">{error}</div>}

      <div className="sh-btn-row">
        <button className="btn btn-s sh-btn" onClick={()=>{setStep(STEPS.CREDENTIALS);setError("");}}>
          {isRTL?"رجوع":"Back"}
        </button>
        <button className="btn btn-p sh-btn" disabled={testing} onClick={saveConnection}>
          {testing?(isRTL?"جارٍ الحفظ...":"Saving..."):(isRTL?"حفظ الاتصال":"Save Connection")}
        </button>
      </div>
    </div>
  );

  /* ── Step: Done ──
     Normally the parent re-renders into the live dashboard as soon as
     smartHomeConnections updates, so this screen flashes for a frame.
     We still render a Continue button as a safety net in case the
     config write is delayed or the parent doesn't remount. */
  return (
    <div className="sh-setup">
      <div className="sh-setup-header">
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="var(--gold)" strokeWidth="1.5"><path d="M6 22l18-16 18 16"/><path d="M10 20v20h28V20"/><rect x="18" y="28" width="12" height="12"/></svg>
        <h2>{isRTL?"المنزل الذكي جاهز!":"Smart Home Ready!"}</h2>
        <p className="sh-setup-loc">{locLabel}</p>
      </div>
      <p className="sh-setup-desc">
        {isRTL
          ?"تم إعداد الاتصال بنجاح. افتح لوحة التحكم بأجهزتك."
          :"Connection saved. Your devices are ready to control."}
      </p>
      <button className="btn btn-p sh-btn" style={{display:"block",margin:"0 auto"}} onClick={()=>{
        /* Force remount by re-selecting the location. Parent will see
           smartHomeConnections[locId].status === "connected" and render
           the dashboard instead of the setup wizard. */
        if(typeof window!=="undefined") window.location.reload();
      }}>
        {isRTL?"فتح لوحة التحكم":"Open Dashboard"}
      </button>
    </div>
  );
}

export default SmartHomeSetup;
